import mapXBackgroundTaskAPI from "api/backgroundTaskApi";
import {STProject} from "api/projectApi/types";
import {AxiosResponse} from "axios";
import classNames from "classnames";
import {withImportErrorReason} from "helpers/importErrorReason";
import {successResponse} from "helpers/map";
import {useAppDispatch, useAppSelector, useModalHook} from "hooks";
import {TUrlImportItem} from "mapx-components/BulkImport/types";
import BulkImportReport from "mapx-components/Modals/BulkImportModal/Report";
import Papa from "papaparse";
import type {FC} from "react";
import {useEffect, useMemo, useState} from "react";
import {setBackgroundBulkImportsInProgress} from "store/background-tasks/backgroundTaskActions";
import {handleCompletedImports} from "store/background-tasks/backgroundTaskAsyncActions";
import {setShouldOpenBulkImportReport} from "store/bulk-import/bulkImportActions";
import {projectSelector} from "store/project-list/projectListSelectors";
import colors from "styles/themes.module.scss";
import {
	backgroundTaskApiParameterOptions,
	STBackgroundTaskResponse,
} from "../../../api/backgroundTaskApi/types";
import {sortUrls} from "../../BulkImport/util";
import Form from "./Form";
import {formatETA, isInProgressStatus, STATUS_MESSAGES} from "./Report/utils";
import styles from "./bulkImportModal.module.scss";
import type {TBulkImportModalProps, TState} from "./types";

/**
 * Modal component for handling bulk import.
 * It manages different states like form submission, report display, and import history download.
 */
const BulkImportModal: FC<TBulkImportModalProps> = ({
	setModalDisplay,
	modalDisplay = true,
	onSubmit,
	inProgress,
	onSuccessClose,
	linkedInUrl,
	bulkImportModalTestId = "",
	initialState = "link-submit",
	source,
	marketMapId,
	renderLastCompletedReport,
	lastCompletedReportData,
	isLastBulkImportDataLoading,
	importPercentage,
	lastCompletedImportID,
	otherImportInstancePresent,
	shouldOpenReportModal,
	labels,
	validateUrl,
}) => {
	const overlayZIndex = source === "market_map" ? {overlay: {zIndex: 101}} : {};

	const {modalIsOpen, customStyles, Modal} = useModalHook(
		{content: {width: "550px", borderRadius: "8px"}, ...overlayZIndex},
		modalDisplay,
		colors.mainThemeColor,
	);

	const [state, setState] = useState<TState>("link-submit");

	const [isSubmitting, setIsSubmitting] = useState(false);

	const [loadingImportHistoryDownload, setLoadingImportHistoryDownload] = useState(false);

	const dispatch = useAppDispatch();

	const project = useAppSelector(projectSelector) as STProject;

	const countUrls = (urls: TUrlImportItem[]): number => {
		return urls?.length;
	};

	const closeModal = () => {
		setModalDisplay(false);
	};

	useEffect(() => {
		setState(initialState);
	}, [initialState]);

	const handleSubmit = async (url: string[]) => {
		setIsSubmitting(true);
		const response: TState = await onSubmit(url);
		setIsSubmitting(false);
		setState(response);
	};

	const handleDoneClick = (projectID: number) => {
		onSuccessClose(projectID);
		setState("link-submit");
	};

	// Opens the report modal if required, after an import completes successfully
	useEffect(() => {
		if (shouldOpenReportModal && state === "success") {
			setModalDisplay(true);
			if (source === "project") {
				const reportID = labels.entityName === "Company" ? -project?.id : project?.id;
				dispatch(setShouldOpenBulkImportReport({id: reportID, value: false}));
			} else if (source === "market_map" && marketMapId) {
				dispatch(setShouldOpenBulkImportReport({id: -marketMapId, value: false}));
			} else {
				dispatch(setShouldOpenBulkImportReport({id: 0, value: false}));
			}
		}
	}, [
		shouldOpenReportModal,
		setModalDisplay,
		state,
		dispatch,
		project,
		source,
		marketMapId,
		labels.entityName,
	]);

	const totalCompletedUrls = useMemo(() => {
		return countUrls(lastCompletedReportData);
	}, [lastCompletedReportData]);

	const getStatusMessage = (status: string) => STATUS_MESSAGES[status.toLowerCase()] || status;

	const getReasonMessage = (status: string, errorReason?: string | null): string => {
		const lowerStatus = getStatusMessage(status).toLowerCase();
		if (lowerStatus === "bad url") {
			return "Not a valid LinkedIn URL";
		} else if (lowerStatus === "not found") {
			return "This profile was not found";
		} else if (lowerStatus === "error") {
			return withImportErrorReason("This url could not be processed", errorReason);
		}

		return "";
	};

	/**
	 * Downloads a CSV report of imported LinkedIn profiles.
	 * Fetches import data from API if not already available, and we fetch last 1000 imports assuming it's enough for now.
	 */
	const downloadReport = async (linkedInUrls?: Nullable<TUrlImportItem[]>) => {
		let toBeDownloadedData = linkedInUrls;

		if (!toBeDownloadedData) {
			setLoadingImportHistoryDownload(true);

			const payload: backgroundTaskApiParameterOptions = {
				projectId: project?.id,
				status: ["Completed"],
				backgroundTaskType: [
					labels.entityName === "Company"
						? "Companies Bulk Import"
						: "Candidates Bulk Import",
				],
			};

			const pagination = {
				page: 1,
				perPage: 1000,
			};

			try {
				const response: AxiosResponse =
					await mapXBackgroundTaskAPI.getAllBackgroundTaskRequests(
						payload,
						pagination,
						{},
					);

				if (successResponse(response, 200)) {
					const bulkImportData: STBackgroundTaskResponse[] = response.data.results;

					const res = await dispatch(
						handleCompletedImports("download", bulkImportData, project),
					);

					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					toBeDownloadedData = sortUrls(res.importInfo.linkedin_candidate_urls);
				} else {
					console.error("Error fetching background bulk imports:");
				}
			} catch (error) {
				console.error("Error fetching background bulk imports:", error);
				dispatch(setBackgroundBulkImportsInProgress(false));
			}

			setLoadingImportHistoryDownload(false);
		}

		if (!toBeDownloadedData) {
			return;
		}

		// Helper function to generate a sanitized filename
		const generateFileName = (projectName: string) => {
			const date = new Date().toLocaleDateString();

			if (projectName) {
				return (
					projectName?.replace(/[.]/g, "_").replace(/[\s,]/g, "_") +
					"_" +
					date.replace(/[\s,]/g, "_")
				);
			} else {
				return (
					(labels.entityName ?? "Candidate") +
					"_import" +
					"_" +
					date.replace(/[\s,]/g, "_")
				);
			}
		};

		// Process the data for CSV
		const csvData = toBeDownloadedData.map((item) => ({
			linkedin_url: item.url,
			status: getStatusMessage(item.status),
			reason: getReasonMessage(item.status, item.error_reason),
			eta: isInProgressStatus(item.status)
				? formatETA(item.eta_expected_at, item.eta_original)
				: "-",
		}));

		// Convert to CSV and trigger download
		const csv = Papa.unparse(csvData);
		const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.setAttribute("href", url);
		a.setAttribute("download", generateFileName(project?.name));
		a.click();
	};

	return (
		<Modal
			data-testid={bulkImportModalTestId}
			style={customStyles}
			isOpen={modalIsOpen}
			contentLabel="Modal"
			onRequestClose={closeModal}
		>
			<div
				data-testid="bulkImportModalComponent"
				className={classNames("modal", styles.wrapper)}
			>
				{renderLastCompletedReport ? (
					// Renders the last completed import report
					<BulkImportReport
						totalURLs={totalCompletedUrls}
						linkedInUrl={lastCompletedReportData}
						handleDownload={(urls) => downloadReport(urls)}
						inProgress={true}
						lastCompletedDisplay={true}
						isLastBulkImportDataLoading={isLastBulkImportDataLoading}
						importPercentage={importPercentage}
						loadingImportHistoryDownload={loadingImportHistoryDownload}
						handleClick={() => handleDoneClick(lastCompletedImportID)}
						importedLinkedInUrls={lastCompletedReportData}
						source={source}
						labels={labels}
					/>
				) : !inProgress ? (
					// Renders the add new url's form
					<Form
						handleSubmit={handleSubmit}
						handleDownload={(urls) => downloadReport(urls)}
						inProgress={inProgress}
						isSubmitting={isSubmitting}
						otherImportInstancePresent={otherImportInstancePresent}
						linkedInUrl={linkedInUrl}
						loadingImportHistoryDownload={loadingImportHistoryDownload}
						source={source}
						marketMapId={marketMapId}
						importedLinkedInUrls={lastCompletedReportData}
						labels={labels}
						validateUrl={validateUrl}
					/>
				) : (
					linkedInUrl && (
						// Renders the live import report
						<BulkImportReport
							totalURLs={countUrls(linkedInUrl)}
							linkedInUrl={sortUrls(linkedInUrl)}
							handleDownload={(urls) => downloadReport(urls)}
							lastCompletedDisplay={false}
							isLastBulkImportDataLoading={isLastBulkImportDataLoading}
							inProgress={inProgress && countUrls(linkedInUrl) > 0}
							importPercentage={importPercentage}
							loadingImportHistoryDownload={loadingImportHistoryDownload}
							handleClick={() =>
								handleDoneClick(
									source === "project"
										? project?.id
										: source === "market_map" && marketMapId
										? marketMapId
										: 0,
								)
							}
							importedLinkedInUrls={lastCompletedReportData}
							source={source}
							labels={labels}
						/>
					)
				)}
			</div>
		</Modal>
	);
};

export default BulkImportModal;
